/* =========================================================
   Custom cart drawer behavior
   - <cart-recommendations>: fetches related products via section API
   - AJAX intercept on "+ Add" buttons inside the drawer
   ========================================================= */
(function () {
  /* ---------- <cart-recommendations> custom element ---------- */
  if (!customElements.get('cart-recommendations')) {
    class CartRecommendations extends HTMLElement {
      connectedCallback() {
        const productId = this.getAttribute('data-product-id');
        if (!productId) return;
        const limit = this.getAttribute('data-limit') || '2';
        const url =
          '/recommendations/products?section_id=cart-recommendations' +
          '&product_id=' + encodeURIComponent(productId) +
          '&limit=' + encodeURIComponent(limit) +
          '&intent=related';

        fetch(url, { credentials: 'same-origin' })
          .then(function (r) { return r.text(); })
          .then(function (html) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const card = doc.querySelector('.cart-drawer__recommendations-card');
            if (card) {
              this.innerHTML = '';
              this.appendChild(card);
            }
          }.bind(this))
          .catch(function () { /* silent */ });
      }
    }
    customElements.define('cart-recommendations', CartRecommendations);
  }

  /* ---------- AJAX add-to-cart for "+ Add" buttons ---------- */

  function getCartDrawer() {
    return document.querySelector('cart-drawer');
  }

  function openDrawer(drawer) {
    if (!drawer) return;
    // Try the theme's own open() method first
    if (typeof drawer.open === 'function') {
      try { drawer.open(); return; } catch (e) { /* fall through */ }
    }
    drawer.classList.add('active', 'animate');
    drawer.setAttribute('open', '');
    document.body.classList.add('overflow-hidden');
  }

  function replaceDrawerContents(html) {
    const drawer = getCartDrawer();
    if (!drawer) return;

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const newDrawer = doc.querySelector('cart-drawer');
    if (!newDrawer) return;

    // Preserve open state
    const wasActive = drawer.classList.contains('active');

    // Update class list (handles is-empty toggle)
    drawer.className = newDrawer.className;
    if (wasActive) drawer.classList.add('active');

    // Swap inner HTML
    drawer.innerHTML = newDrawer.innerHTML;

    // Re-trigger any custom elements inside (cart-recommendations etc.)
    // by re-parsing & replacing with cloned nodes
    drawer.querySelectorAll('cart-recommendations').forEach(function (el) {
      const clone = el.cloneNode(true);
      el.parentNode.replaceChild(clone, el);
    });
  }

  function updateCartIconBubble(sectionHtml) {
    if (!sectionHtml) return;
    const parser = new DOMParser();
    const doc = parser.parseFromString(sectionHtml, 'text/html');
    // The section may wrap content in a known ID, or just contain the bubble element
    const candidates = [
      '#shopify-section-cart-icon-bubble',
      '#cart-icon-bubble',
      '.cart-count-bubble',
    ];
    for (const sel of candidates) {
      const fresh = doc.querySelector(sel);
      const existing = document.querySelector(sel);
      if (fresh && existing) {
        existing.innerHTML = fresh.innerHTML;
        return;
      }
    }
    // Fallback: replace any element with id cart-icon-bubble inside header
    const fresh = doc.body.firstElementChild;
    const existing = document.getElementById('cart-icon-bubble');
    if (fresh && existing) existing.innerHTML = fresh.innerHTML;
  }

  async function ajaxAddToCart(form) {
    const button = form.querySelector('button[type="submit"]');
    if (!button) return;
    if (button.dataset.loading === 'true') return;

    const originalText = button.textContent;
    button.dataset.loading = 'true';
    button.textContent = 'Adding…';

    try {
      const formData = new FormData(form);
      formData.append('sections', 'cart-drawer,cart-icon-bubble');
      formData.append('sections_url', window.location.pathname);

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data && (data.description || data.message) || 'Add to cart failed');
      }

      // Update drawer in place
      if (data.sections && data.sections['cart-drawer']) {
        replaceDrawerContents(data.sections['cart-drawer']);
      }

      // Update cart icon bubble in header
      if (data.sections && data.sections['cart-icon-bubble']) {
        updateCartIconBubble(data.sections['cart-icon-bubble']);
      }

      // Make sure drawer is open (it usually already is)
      openDrawer(getCartDrawer());

      // Notify any theme listeners
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));

      button.textContent = 'Added';
      setTimeout(function () {
        if (button.isConnected) button.textContent = originalText;
      }, 1200);
    } catch (err) {
      console.error('Cart add error:', err);
      button.textContent = 'Try again';
      setTimeout(function () {
        if (button.isConnected) button.textContent = originalText;
      }, 1500);
    } finally {
      button.dataset.loading = 'false';
    }
  }

  // Delegate so it works after re-renders
  document.addEventListener('submit', function (e) {
    const form = e.target.closest('[data-cart-rec-form]');
    if (!form) return;
    e.preventDefault();
    e.stopPropagation();
    ajaxAddToCart(form);
  });

  // Stop card-wrapping links from intercepting clicks on the add-to-cart
  // button (Dawn's product cards use a `::after` overlay link that
  // covers the whole card).
  document.addEventListener('click', function (e) {
    const button = e.target.closest('.card__quick-add-button');
    if (!button) return;
    e.stopPropagation();
  }, true);

  /* ---------- Discount code apply ---------- */

  function getStoredDiscount() {
    try { return localStorage.getItem('cart_discount_code') || ''; } catch (e) { return ''; }
  }

  function setStoredDiscount(code) {
    try { localStorage.setItem('cart_discount_code', code); } catch (e) {}
  }

  function showDiscountStatus(text, state) {
    const el = document.querySelector('[data-cart-discount-status]');
    if (!el) return;
    el.textContent = text;
    if (state) el.setAttribute('data-state', state);
    else el.removeAttribute('data-state');
    el.hidden = !text;
  }

  // No active discount codes are configured yet — short-circuit with a
  // clear error message instead of redirecting through Shopify's
  // /discount/CODE endpoint. Flip DISCOUNTS_ENABLED to true once codes
  // are set up in admin and the live behavior is needed again.
  const DISCOUNTS_ENABLED = false;

  function applyDiscount(code) {
    const trimmed = (code || '').trim();
    if (!trimmed) {
      showDiscountStatus('Please enter a code', 'error');
      return;
    }

    if (!DISCOUNTS_ENABLED) {
      showDiscountStatus('Coupon code does not exist', 'error');
      return;
    }

    setStoredDiscount(trimmed);
    showDiscountStatus('Applying ' + trimmed + '…', 'pending');
    const redirect = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.href = '/discount/' + encodeURIComponent(trimmed) + '?redirect=' + redirect;
  }

  // Restore "applied" message on page load if a code is remembered.
  function restoreDiscountStatus() {
    if (!DISCOUNTS_ENABLED) return; // don't restore a "saved code" while disabled
    const code = getStoredDiscount();
    if (!code) return;
    const status = document.querySelector('[data-cart-discount-status]');
    const input = document.querySelector('[data-cart-discount-form] input[name="discount_code"]');
    if (input && !input.value) input.value = code;
    if (status) {
      status.textContent = 'Code "' + code + '" will apply at checkout';
      status.setAttribute('data-state', 'success');
      status.hidden = false;
    }
  }

  document.addEventListener('submit', function (e) {
    const form = e.target.closest('[data-cart-discount-form]');
    if (!form) return;
    e.preventDefault();
    const input = form.querySelector('input[name="discount_code"]');
    if (!input) return;
    applyDiscount(input.value);
  });

  // Run restore both on initial load and after the drawer re-renders
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', restoreDiscountStatus);
  } else {
    restoreDiscountStatus();
  }
  document.addEventListener('cart:updated', restoreDiscountStatus);
})();
